// ============================================================================
// geometry/bumper-plate.js — code-generated competition bumper plate (Phase E)
// ============================================================================
// Modeled from Ryan's reference "Premium Bumper Plate" (measured from the image
// 2026-07-11 — every ratio below is a real measurement, R = plate radius):
//
//   RADIAL ZONES (fraction of R)          Z-PROFILE (front)
//   ├─ bore hole      0     → 0.114        collar (metal), 50.4 mm dia
//   ├─ ZINC HUB       0.114 → 0.457        seated ⇩ hubDrop (1/32″) below the rim
//   │   └─ 6 bolts @ 0.289 R (65 mm), 60° apart, two on the horizontal axis.
//   │        Socket-head cap screw in a COUNTERBORE: flat zinc steps DOWN into a
//   │        pocket, a thin clearance gap ring, then the round head rises back up
//   │        to stand slightly PROUD (~1/64″) of the zinc — its top OUTER edge
//   │        rounded — with a hex (allen) socket sunk into its center. FRONT only.
//   ├─ ring C (thin)  0.457 → 0.505        RAISED  (top plane)
//   ├─ ring B (DESIGN)0.505 → 0.873        RECESSED −⅛″ — the BrandFace
//   └─ ring A (medium)0.873 → 1.000        RAISED  (top plane), rounded outer edge
//
// The two thin lines the artist drew at 0.505 / 0.873 are groove shadows where
// ring B drops below A & C — reproduced as real geometry: the ⅛″ drops are FLAT
// CHAMFERS slanted ~20° off vertical (Ryan 2026-07-11), crisp-edged (a rounded lip
// there sweeps its normal too fast and reads as a dark-light-dark band).
//
// FAKE AO (Ryan 2026-07-11): no real-time shadows (perf budget), so deep-recess
// surfaces (bolt gap ring, hex socket, bore collar) carry a dark VERTEX COLOR.
// The crevices then read dark from any angle — head-on you still see the bolts
// and hex holes because light lands on the flats, not down the cracks.
//
// THREE MATERIAL SLOTS:
//   0 BrandFace — ring B recessed front floor only, the design (planar UV)
//   1 body      — black satin rubber (rings A/C, rounded steps, edge, rim, back)
//   2 hub       — shiny zinc (hub faces + bore collar + the 6 bolts); vertexColors
//
// The whole plate (minus bolts) is a SURFACE OF REVOLUTION: every ring/wall is
// emitted by one `emitBand` revolver over a cross-section profile {r,z,nr,nz}.
// buildBumperPlateMesh is PURE (no THREE / WebGL) so it is node-testable
// (test/reveal-3d.smoke.mjs); the THREE factory takes THREE as an argument.
// Winding is bulletproof: every quad/tri is emitted with a desired outward
// normal and auto-reversed if the geometric normal opposes it.
//
// TRI BUDGET (V2 plan §8, <50k): ~11k triangles at the defaults.
// ============================================================================

/** Default real-world dimensions (metres) + tessellation. All radial features
 *  are fractions of `outerRadius`; all Z features are absolute metres. */
export const DEFAULT_BUMPER_OPTS = Object.freeze({
  // --- overall (competition plate: 450 mm dia, thickness varies by weight) ---
  outerRadius: 0.225, // 450 mm plate
  boreRadius: 0.0252, // 50.4 mm Olympic collar insert (radius) = 0.112 R
  thickness: 0.0531, // hero = 45 lb / 2.09″ (55=0.0635, 35=0.0424, 25=0.0351)
  radialSegments: 160, // ring smoothness + tri count
  edgeRadius: 0.0051, // 5.1 mm rounded outer edge (Ryan −15% from 6 mm)
  filletSegments: 4, // sub-segments per quarter-round outer edge

  // --- radial ring boundaries (fraction of R, measured from the reference) ---
  hubOuterFrac: 0.457, // zinc hub outer radius
  ringCouterFrac: 0.505, // ring C / ring B groove
  ringBouterFrac: 0.873, // ring B / ring A groove

  // --- Z steps (absolute metres) ---
  recess: 0.0032, // ⅛″ — ring B design channel below the raised A/C plane
  hubDrop: 0.0008, // 1/32″ — rubber rim stands only this far proud of the zinc hub (Ryan; both faces)
  channelSlantDeg: 20, // design-channel drop walls slant this far OFF VERTICAL (Ryan; 0 = vertical), flat chamfer
  boreFillet: 0.001, // subtle round-over where the flat hub meets the bore (inner circle)

  // --- Economy plate variant (Ryan 2026-07-14) --------------------------------
  // Same rings A/B (design), slanted channel, edge, rim + back — but NO zinc hub and
  // NO bolts: the center is rubber all the way across, with a small rounded METAL
  // FLANGE lining the bore (a ¼″ round-over onto each face, through the bore, and
  // onto the back). Thickness varies by weight (ECONOMY_THICKNESS) and is thicker
  // than the competition plate. Design still prints on ring B only.
  economy: false,
  flangeWrap: 0.00635, // ¼″ — the metal bore flange's wrap onto each face + round-over radius

  // --- bolts (front hub only): socket-head cap screws, heads standing slightly proud.
  boltCircleFrac: 0.289, // 65 mm bolt-circle radius
  boltCount: 6,
  boltHeadRadius: 0.0079, // ~15.8 mm round head diameter (Ryan: slightly larger)
  boltGap: 0.0006, // tighter clearance ring between head edge and counterbore (Ryan)
  boltCounterboreDepth: 0.0016, // counterbore pocket depth below the hub plane
  boltProud: 0.000397, // 1/64″ — bolt-head top stands this far PROUD of the hub (Ryan)
  boltHeadEdgeRadius: 0.0006, // round-over on the head's top outer edge (Ryan)
  boltHexRadius: 0.0045, // allen socket circumradius (~9 mm across)
  boltSocketDepth: 0.006, // hex (allen) socket cut depth into the head (deep)
  boltSegments: 24, // angular tessellation of each bolt
});

/** Plate thickness (metres) by lb denomination — measured from Ryan's spec
 *  (2026-07-14). Economy plates are thicker (cheaper, less-dense rubber). The
 *  geometry rebuilds to the right thickness when the buyer changes denomination.
 *  Competition plates only come in 25–55 lb (no 10/15 change plates). */
export const COMPETITION_THICKNESS = Object.freeze({
  /* ⚠ PROVISIONAL — DERIVED, NOT MEASURED (2026-08-02). Every other row here is a real dimension.
     This one exists because Ryan added the 10 lb competition plate to the designer (it is on the
     price list as PUC10LB / PUB10LB, urethane only) and a denomination with no row silently renders
     at the nearest charted thickness — a 25's 1.38″ on a plate that is nothing like it.
       Derived from the competition:economy thickness ratio, which is remarkably steady across the
     four measured pairs — 0.763 / 0.745 / 0.719 / 0.760, mean 0.747 — applied to the economy 10's
     0.0231 m. That is a defensible estimate and it is still an estimate.
       IT IS ONLY EVER SEEN EDGE-ON (the face is 450 mm at every denomination), so being wrong here
     costs a slightly-too-thin silhouette and nothing else. Replace with the measured figure when
     Ryan has it; nothing else needs to change. */
  10: 0.0173, // 0.68″ — PROVISIONAL, see above
  25: 0.0351, // 1.38″
  35: 0.0424, // 1.67″
  45: 0.0531, // 2.09″ (hero)
  55: 0.0635, // 2.50″
});
export const ECONOMY_THICKNESS = Object.freeze({
  10: 0.0231, // 0.91″
  15: 0.0295, // 1.16″
  25: 0.0460, // 1.81″
  35: 0.0569, // 2.24″
  45: 0.0739, // 2.91″ (hero)
  55: 0.0836, // 3.29″
});

// KG denominations map to their nearest lb bumper (thickness ≈ the lb equivalent).
const KG_TO_LB = { 5: 10, 10: 25, 15: 35, 20: 45, 25: 55 };

/** Resolves the plate thickness (metres) for a product + denomination, falling back
 *  to the nearest available weight when a table has no exact entry (e.g. a
 *  competition 10 lb → the 25 lb thickness). */
export function plateThickness(economy, weight, unit) {
  const lb = unit === 'kg' ? KG_TO_LB[weight] || 45 : weight;
  const table = economy ? ECONOMY_THICKNESS : COMPETITION_THICKNESS;
  if (table[lb] != null) return table[lb];
  const keys = Object.keys(table).map(Number);
  const nearest = keys.reduce((a, b) => (Math.abs(b - lb) < Math.abs(a - lb) ? b : a), keys[0]);
  return table[nearest];
}

/** Material slot indices (mirror the BufferGeometry groups). */
export const MATERIAL_BRAND_FACE = 0;
export const MATERIAL_BODY = 1;
export const MATERIAL_HUB = 2;

// Fake-AO vertex-color darkness for crevices (1 = full lit zinc).
const AO_FLAT = 1.0;
const AO_BORE = 0.65; // bore collar (inside the center hole)
const AO_HEAD_WALL = 0.6; // bolt head side wall
const AO_GAP = 0.3; // counterbore wall + clearance gap floor
const AO_HEX = 0.2; // hex (allen) socket — dark but bigger hole lets more light in

// --- tiny vec helpers (no deps) ---------------------------------------------
function sub(a, b) {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function cross(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function normalize3(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

/**
 * Pure vertex-math generator for the competition bumper plate.
 *
 * @param {Partial<typeof DEFAULT_BUMPER_OPTS>} [opts]
 * @returns {{
 *   positions: number[], normals: number[], uvs: number[], colors: number[],
 *   indices: number[],
 *   groups: {start:number,count:number,materialIndex:number,name:string}[],
 *   triangleCount: number, vertexCount: number,
 *   brandVertexRange: {start:number,count:number},
 *   params: typeof DEFAULT_BUMPER_OPTS,
 * }}
 */
export function buildBumperPlateMesh(opts = {}) {
  const params = { ...DEFAULT_BUMPER_OPTS, ...opts };
  const R = params.outerRadius;
  const rB = params.boreRadius;
  const t2 = params.thickness / 2;
  const seg = Math.max(24, Math.floor(params.radialSegments));

  // Absolute radii from the measured fractions.
  const rHub = params.hubOuterFrac * R; // zinc hub outer
  const rC = params.ringCouterFrac * R; // C/B groove
  const rBb = params.ringBouterFrac * R; // B/A groove

  // Z levels.
  const recess = Math.max(0, Math.min(params.recess, 0.6 * t2));
  const hubDrop = Math.max(0, Math.min(params.hubDrop, 0.6 * t2));
  const zTop = t2; // raised plane (rings A & C)
  const zDesign = t2 - recess; // recessed ring B (design)
  const zHub = t2 - hubDrop; // zinc hub front plane

  // Outer edge rounding + groove-corner rounding.
  const er = Math.max(0, Math.min(params.edgeRadius, 0.9 * t2, 0.9 * (R - rBb)));
  const fSeg = er > 1e-6 ? Math.max(1, Math.floor(params.filletSegments)) : 0;
  const rFlatA = R - er; // ring A flat extends to here; the fillet carries it to R
  // Design-channel drop SLANT (Ryan): the two ⅛″ drops from rings A & C into the
  // recessed ring-B floor lean this far off vertical (positive draft — the channel
  // opens slightly wider at the rim than at the floor), as a FLAT CHAMFER (no corner
  // round-over — see emitSlantedStep). `slantRun` is the radial travel over the ⅛″
  // drop; the flat FLOOR extents are precomputed here because the brand annulus is
  // emitted first (its verts must stay contiguous from vertex 0).
  const slantRad = Math.max(0, Math.min((params.channelSlantDeg * Math.PI) / 180, (60 * Math.PI) / 180));
  const slantRun = Math.min(recess * Math.tan(slantRad), 0.35 * (rBb - rC));
  const cbRaisedR = rC; // ring C flat ends at the chamfer's raised corner
  const cbFloorR = rC + slantRun; // ring B floor inner edge (chamfer floor corner)
  const baFloorR = rBb - slantRun; // ring B floor outer edge
  const baRaisedR = rBb; // ring A flat starts at the chamfer's raised corner

  const positions = [];
  const normals = [];
  const uvs = [];
  const colors = [];
  const tangents = [];
  const idxBrand = [];
  const idxBody = [];
  const idxHub = [];
  const state = { vertCount: 0 };

  const routeIdx = (slot) => (slot === 'brand' ? idxBrand : slot === 'hub' ? idxHub : idxBody);
  const planarUV = (x, y) => [x / (2 * R) + 0.5, y / (2 * R) + 0.5];

  function pushVert(pos, normal, uv, ao) {
    positions.push(pos[0], pos[1], pos[2]);
    const n = normalize3(normal);
    normals.push(n[0], n[1], n[2]);
    uvs.push(uv[0], uv[1]);
    colors.push(ao, ao, ao);
    // Circumferential tangent → anisotropic "turned-metal" reflection on the hub.
    const tl = Math.hypot(pos[0], pos[1]) || 1;
    tangents.push(-pos[1] / tl, pos[0] / tl, 0, 1);
    state.vertCount += 1;
  }

  /** Emits one quad (2 tris) with correct outward winding. */
  function pushQuad(slot, corners, ao = 1) {
    const desired = normalize3([
      corners[0].normal[0] + corners[1].normal[0] + corners[2].normal[0] + corners[3].normal[0],
      corners[0].normal[1] + corners[1].normal[1] + corners[2].normal[1] + corners[3].normal[1],
      corners[0].normal[2] + corners[1].normal[2] + corners[2].normal[2] + corners[3].normal[2],
    ]);
    const geo = cross(sub(corners[1].pos, corners[0].pos), sub(corners[2].pos, corners[0].pos));
    const c = dot(geo, desired) < 0 ? [corners[0], corners[3], corners[2], corners[1]] : corners;
    const base = state.vertCount;
    for (const v of c) pushVert(v.pos, v.normal, v.uv, ao);
    routeIdx(slot).push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  /** Emits one triangle (used for the hex socket bottom fan). */
  function pushTri(slot, corners, ao = 1) {
    const desired = normalize3([
      corners[0].normal[0] + corners[1].normal[0] + corners[2].normal[0],
      corners[0].normal[1] + corners[1].normal[1] + corners[2].normal[1],
      corners[0].normal[2] + corners[1].normal[2] + corners[2].normal[2],
    ]);
    const geo = cross(sub(corners[1].pos, corners[0].pos), sub(corners[2].pos, corners[0].pos));
    const c = dot(geo, desired) < 0 ? [corners[0], corners[2], corners[1]] : corners;
    const base = state.vertCount;
    for (const v of c) pushVert(v.pos, v.normal, v.uv, ao);
    routeIdx(slot).push(base, base + 1, base + 2);
  }

  // Precompute the shared ring of angles.
  const cosA = new Array(seg + 1);
  const sinA = new Array(seg + 1);
  for (let i = 0; i <= seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    cosA[i] = Math.cos(a);
    sinA[i] = Math.sin(a);
  }
  const CUV = [0.5, 0.5]; // body/hub UVs parked at texture center (solid color)

  /** Revolves a cross-section segment A→B (each {r,z,nr,nz}) 360° about Z. */
  function emitBand(slot, A, B, ao = 1, mirrorU = false) {
    const planar = slot === 'brand' || slot === 'hub'; // hub gets radial UVs for the turning-marks map
    // A back-face design band mirrors U so the artwork reads correctly once the
    // plate spins around to its back — there is no true front/back (Ryan).
    const uvAt = (x, y) => {
      if (!planar) return CUV;
      const u = planarUV(x, y);
      return mirrorU ? [1 - u[0], u[1]] : u;
    };
    for (let i = 0; i < seg; i++) {
      const c0 = cosA[i];
      const s0 = sinA[i];
      const c1 = cosA[i + 1];
      const s1 = sinA[i + 1];
      const a0 = [A.r * c0, A.r * s0, A.z];
      const a1 = [A.r * c1, A.r * s1, A.z];
      const b0 = [B.r * c0, B.r * s0, B.z];
      const b1 = [B.r * c1, B.r * s1, B.z];
      pushQuad(
        slot,
        [
          { pos: a0, uv: uvAt(a0[0], a0[1]), normal: [A.nr * c0, A.nr * s0, A.nz] },
          { pos: b0, uv: uvAt(b0[0], b0[1]), normal: [B.nr * c0, B.nr * s0, B.nz] },
          { pos: b1, uv: uvAt(b1[0], b1[1]), normal: [B.nr * c1, B.nr * s1, B.nz] },
          { pos: a1, uv: uvAt(a1[0], a1[1]), normal: [A.nr * c1, A.nr * s1, A.nz] },
        ],
        ao,
      );
    }
  }

  /** Quarter-round arc of profile points, center (cr,cz), radius er, a0→a1. */
  function arcPoints(cr, cz, a0, a1) {
    const pts = [];
    for (let k = 0; k <= fSeg; k++) {
      const a = a0 + (a1 - a0) * (k / fSeg);
      pts.push({ r: cr + er * Math.cos(a), z: cz + er * Math.sin(a), nr: Math.cos(a), nz: Math.sin(a) });
    }
    return pts;
  }

  /**
   * Emits one SLANTED design-channel drop as a FLAT CHAMFER (Ryan): the ⅛″ step
   * from a raised ring (zTop) down to the recessed ring-B floor (zDesign), leaning
   * `slantRad` off vertical. A single flat facet (no corner round-overs) — a curved
   * lip there sweeps its normal ~70° over <1 mm and catches a different environment
   * reflection than the flat slant, reading as a dark-light-dark band; one facet
   * shades as ONE clean tone with crisp edges. `rEdge` is the raised-ring corner
   * radius; `floorOutward` = the floor lies OUTWARD of rEdge (C/B) vs inward (B/A).
   * `faceN` = +1 front / −1 back (built in face-local zeta so one path serves both).
   */
  function emitSlantedStep(rEdge, faceN, slot, floorOutward) {
    const floorSign = floorOutward ? 1 : -1;
    const L = Math.hypot(slantRun, recess) || 1;
    // Outward wall normal (faces the open channel): mostly radial, tilted up by the
    // slant. nr = floorSign·cosθ, nzeta = sinθ  ⇒  in (recess,run) terms below.
    const nr = (floorSign * recess) / L;
    const nzeta = slantRun / L;
    const rFloor = rEdge + floorSign * slantRun;
    // Single band: raised-ring corner (zTop, rEdge) → floor corner (zDesign, rFloor).
    emitBand(
      slot,
      { r: rEdge, z: faceN * zTop, nr, nz: faceN * nzeta },
      { r: rFloor, z: faceN * zDesign, nr, nz: faceN * nzeta },
    );
  }

  // ===========================================================================
  // 1. BRAND slot FIRST — ring B recessed front FLOOR (design), inset to the
  //    SLANTED walls' flat-floor extents so the design never rides up a bevel.
  // ===========================================================================
  emitBand('brand', { r: cbFloorR, z: zDesign, nr: 0, nz: 1 }, { r: baFloorR, z: zDesign, nr: 0, nz: 1 });
  const brandVertexCount = state.vertCount;

  // ===========================================================================
  // 2. BODY slot — black rubber (rings A/C, slanted steps, edge, rim, back).
  // ===========================================================================
  // Economy plate (Ryan 2026-07-14): rubber runs flat all the way in to a small metal
  // bore FLANGE — no zinc hub, no bolts. The rubber's inner radius is the flange's
  // outer edge (rFlangeOuter); the competition plate stops at the zinc hub (rHub).
  const economy = !!params.economy;
  const flangeWrap = economy ? Math.max(0, Math.min(params.flangeWrap, 0.4 * (rHub - rB), 0.6 * t2)) : 0;
  const rFlangeOuter = rB + flangeWrap;
  const innerRubberR = economy ? rFlangeOuter : rHub;
  // -- FRONT rubber --
  emitBand('body', { r: innerRubberR, z: zTop, nr: 0, nz: 1 }, { r: cbRaisedR, z: zTop, nr: 0, nz: 1 }); // ring C top (→ hub or flange)
  emitSlantedStep(rC, 1, 'body', true); // C/B slanted drop (floor outward)
  emitSlantedStep(rBb, 1, 'body', false); // B/A slanted drop (floor inward)
  emitBand('body', { r: baRaisedR, z: zTop, nr: 0, nz: 1 }, { r: rFlatA, z: zTop, nr: 0, nz: 1 }); // ring A top
  // Top outer fillet (+Z → +X) — rounded edge, black rubber.
  if (fSeg > 0) {
    const top = arcPoints(rFlatA, zTop - er, Math.PI / 2, 0);
    for (let k = 0; k < top.length - 1; k++) emitBand('body', top[k], top[k + 1]);
  }
  // Outer rim cylinder.
  emitBand('body', { r: R, z: zTop - er, nr: 1, nz: 0 }, { r: R, z: -t2 + er, nr: 1, nz: 0 });
  // Bottom outer fillet (+X → −Z).
  if (fSeg > 0) {
    const bot = arcPoints(rFlatA, -t2 + er, 0, -Math.PI / 2);
    for (let k = 0; k < bot.length - 1; k++) emitBand('body', bot[k], bot[k + 1]);
  }
  // -- BACK rubber (mirror; NO design, NO bolts) — same slanted channel --
  emitBand('body', { r: rFlatA, z: -t2, nr: 0, nz: -1 }, { r: baRaisedR, z: -t2, nr: 0, nz: -1 }); // ring A back
  emitSlantedStep(rBb, -1, 'body', false); // B/A slanted drop back
  emitBand('brand', { r: baFloorR, z: -zDesign, nr: 0, nz: -1 }, { r: cbFloorR, z: -zDesign, nr: 0, nz: -1 }, 1, true); // ring B back — DESIGN on BOTH faces (mirrored U reads from behind)
  emitSlantedStep(rC, -1, 'body', true); // C/B slanted drop back
  emitBand('body', { r: cbRaisedR, z: -t2, nr: 0, nz: -1 }, { r: innerRubberR, z: -t2, nr: 0, nz: -1 }); // ring C back (→ hub or flange)
  // Rubber inner walls that drop to the seated zinc hub (front + back). Competition
  // only — the economy plate has no hub (rubber runs flat to the flange).
  if (!economy) {
    emitBand('body', { r: rHub, z: zTop, nr: -1, nz: 0 }, { r: rHub, z: zHub, nr: -1, nz: 0 });
    emitBand('body', { r: rHub, z: -t2, nr: -1, nz: 0 }, { r: rHub, z: -zHub, nr: -1, nz: 0 });
  }

  // ===========================================================================
  // 3. HUB / FLANGE slot — shiny zinc. Economy: just a small rounded bore flange
  //    (no hub, no bolts). Competition: the zinc hub + bore collar + 6 bolts.
  // ===========================================================================
  if (economy) {
    // Economy metal FLANGE — a rounded eyelet lining the bore (Ryan 2026-07-14): a
    // ¼″ round-over onto the FRONT face, straight through the bore as a collar, then
    // a ¼″ round-over onto the BACK. Its outer edge (rFlangeOuter) meets the rubber.
    const fw = flangeWrap;
    const fN = 4; // round-over sub-segments
    let prevF = null;
    for (let k = 0; k <= fN; k++) { // front round-over: (rB+fw, zTop) +Z → (rB, zTop−fw) −R
      const a = Math.PI / 2 + (Math.PI / 2) * (k / fN);
      const p = { r: rFlangeOuter + fw * Math.cos(a), z: zTop - fw + fw * Math.sin(a), nr: Math.cos(a), nz: Math.sin(a) };
      if (prevF) emitBand('hub', prevF, p);
      prevF = p;
    }
    emitBand('hub', { r: rB, z: zTop - fw, nr: -1, nz: 0 }, { r: rB, z: -t2 + fw, nr: -1, nz: 0 }, AO_BORE); // bore collar
    prevF = null;
    for (let k = 0; k <= fN; k++) { // back round-over: (rB, −t2+fw) −R → (rB+fw, −t2) −Z
      const a = Math.PI + (Math.PI / 2) * (k / fN);
      const p = { r: rFlangeOuter + fw * Math.cos(a), z: -t2 + fw + fw * Math.sin(a), nr: Math.cos(a), nz: Math.sin(a) };
      if (prevF) emitBand('hub', prevF, p);
      prevF = p;
    }
  } else {
  const rBC = params.boltCircleFrac * R;
  const boltCount = Math.max(0, Math.floor(params.boltCount));
  const rHead = params.boltHeadRadius;
  const rBore = rHead + params.boltGap; // counterbore (hole) radius
  const boreDepth = Math.min(params.boltCounterboreDepth, 0.5 * hubDrop + 0.5 * t2);
  const socketDepth = Math.min(params.boltSocketDepth, zHub + t2 - 0.001);
  const zBoreFloor = zHub - boreDepth;
  // Bolt heads stand PROUD of the hub by boltProud (Ryan: ~1/64″), and their top
  // OUTER edge carries a small round-over (headEdgeR). The hex socket + floor are
  // measured DOWN from the raised head top (zHead), so the allen depth is constant.
  const boltProud = Math.max(0, params.boltProud || 0);
  const zHead = zHub + boltProud;
  const zHexFloor = zHead - socketDepth;
  const bSeg = Math.max(6, Math.floor(params.boltSegments));
  const headEdgeR = Math.max(
    0,
    Math.min(params.boltHeadEdgeRadius || 0, 0.35 * rHead, 0.6 * (zHead - zBoreFloor)),
  );
  const heSeg = headEdgeR > 1e-6 ? 3 : 0; // arc steps around the head-edge round-over
  const zWallTop = zHead - headEdgeR; // where the vertical head wall meets the edge fillet
  const hexR = (theta) => {
    const s = Math.PI / 3;
    const phi = theta - s * Math.round(theta / s); // ∈ [−30°, 30°]
    return (params.boltHexRadius * Math.cos(s / 2)) / Math.cos(phi);
  };
  const NZ = [0, 0, 1];

  // Round-over where the flat hub meets the bore (the inner "donut" circle). The
  // depth clamp is 1.2×hubDrop (was 0.6× — Ryan 2026-07-13: ~2× rounder). This is
  // what actually sizes the fillet (boreFillet 1 mm sits above it); geometrically
  // safe since bf ≪ zHub (the collar depth).
  const bf = Math.max(0, Math.min(params.boreFillet, 1.2 * hubDrop, 0.3 * (rHub - rB)));
  const bfSeg = bf > 1e-6 ? 3 : 0;
  const rBi = rB + bf; // flat hub face inner radius (the fillet spans rB → rBi)

  emitBand('hub', { r: rHub, z: -zHub, nr: 0, nz: -1 }, { r: rBi, z: -zHub, nr: 0, nz: -1 }); // hub back
  if (bf > 1e-6) {
    // front bore fillet (rBi,zHub) → (rB, zHub−bf); collar; back fillet (mirror)
    let prev = null;
    for (let k = 0; k <= bfSeg; k++) {
      const a = Math.PI / 2 + (Math.PI / 2) * (k / bfSeg);
      const p = { r: rBi + bf * Math.cos(a), z: zHub - bf + bf * Math.sin(a), nr: Math.cos(a), nz: Math.sin(a) };
      if (prev) emitBand('hub', prev, p);
      prev = p;
    }
    emitBand('hub', { r: rB, z: zHub - bf, nr: -1, nz: 0 }, { r: rB, z: -zHub + bf, nr: -1, nz: 0 }, AO_BORE); // collar
    prev = null;
    for (let k = 0; k <= bfSeg; k++) {
      const a = Math.PI + (Math.PI / 2) * (k / bfSeg);
      const p = { r: rBi + bf * Math.cos(a), z: -zHub + bf + bf * Math.sin(a), nr: Math.cos(a), nz: Math.sin(a) };
      if (prev) emitBand('hub', prev, p);
      prev = p;
    }
  } else {
    emitBand('hub', { r: rB, z: zHub, nr: -1, nz: 0 }, { r: rB, z: -zHub, nr: -1, nz: 0 }, AO_BORE);
  }

  // -- Hub FRONT face at zHub with a TRUE circular hole per bolt. -------------
  // Inner + outer annulus, plus a middle band: background quads where there is
  // no bolt, and per bolt a fan from the bolt's true circle (sampled in BOLT-
  // LOCAL angle → perfectly round) out to the surrounding wedge frame. Bolt-
  // local sampling avoids the cusped "cat-eye" a plate-radial cut produces at
  // the circle's tangent lines.
  if (boltCount > 0) {
    const margin = 0.0015;
    const r1 = Math.max(rBi + 1e-4, rBC - rBore - margin);
    const r2 = Math.min(rHub - 1e-4, rBC + rBore + margin);
    emitBand('hub', { r: rBi, z: zHub, nr: 0, nz: 1 }, { r: r1, z: zHub, nr: 0, nz: 1 }); // inner annulus
    emitBand('hub', { r: r2, z: zHub, nr: 0, nz: 1 }, { r: rHub, z: zHub, nr: 0, nz: 1 }); // outer annulus
    const segAng = (2 * Math.PI) / seg;
    const footHalf = Math.asin(Math.min(1, rBore / rBC));
    const half = Math.ceil(footHalf / segAng) + 1; // whole segments each side of a bolt
    const owner = new Array(seg).fill(false);
    const centers = [];
    for (let b = 0; b < boltCount; b++) {
      const beta = (b / boltCount) * 2 * Math.PI;
      const kc = ((Math.round(beta / segAng) % seg) + seg) % seg;
      centers.push({ beta, kc });
      for (let k = kc - half; k <= kc + half; k++) owner[((k % seg) + seg) % seg] = true;
    }
    const flatQuad = (ri, ro, i) => {
      const c0 = cosA[i];
      const s0 = sinA[i];
      const c1 = cosA[i + 1];
      const s1 = sinA[i + 1];
      pushQuad('hub', [
        { pos: [ri * c0, ri * s0, zHub], uv: planarUV(ri * c0, ri * s0), normal: NZ },
        { pos: [ri * c1, ri * s1, zHub], uv: planarUV(ri * c1, ri * s1), normal: NZ },
        { pos: [ro * c1, ro * s1, zHub], uv: planarUV(ro * c1, ro * s1), normal: NZ },
        { pos: [ro * c0, ro * s0, zHub], uv: planarUV(ro * c0, ro * s0), normal: NZ },
      ]);
    };
    for (let i = 0; i < seg; i++) if (!owner[i]) flatQuad(r1, r2, i); // background middle band
    // per bolt: true circle (bolt-local) → surrounding wedge frame (ray from C)
    for (const { beta, kc } of centers) {
      const Cx = rBC * Math.cos(beta);
      const Cy = rBC * Math.sin(beta);
      const thA = (kc - half) * segAng;
      const thB = (kc + half + 1) * segAng;
      const midTh = (thA + thB) / 2;
      const halfSpan = (thB - thA) / 2;
      // Nearest wedge-boundary hit along dir, VALIDATED to lie on the actual
      // boundary segment (radius ∈ [r1,r2] and angle ∈ [thA,thB]) — accepting
      // the infinite edge-line intersections unchecked was poking stray slivers
      // at the four wedge corners.
      const frameHit = (dx, dy) => {
        let best = Infinity;
        const consider = (s) => {
          if (s <= 1e-9 || s >= best) return;
          const px = Cx + s * dx;
          const py = Cy + s * dy;
          const rr = Math.hypot(px, py);
          if (rr < r1 - 1e-6 || rr > r2 + 1e-6) return;
          let dd = Math.atan2(py, px) - midTh;
          while (dd > Math.PI) dd -= 2 * Math.PI;
          while (dd < -Math.PI) dd += 2 * Math.PI;
          if (Math.abs(dd) > halfSpan + 1e-6) return;
          best = s;
        };
        const Cd = Cx * dx + Cy * dy;
        const cLen = Cx * Cx + Cy * Cy;
        let disc = Cd * Cd - (cLen - r2 * r2);
        if (disc >= 0) consider(-Cd + Math.sqrt(disc)); // outer arc
        disc = Cd * Cd - (cLen - r1 * r1);
        if (disc >= 0) {
          consider(-Cd - Math.sqrt(disc)); // inner arc (near + far roots)
          consider(-Cd + Math.sqrt(disc));
        }
        for (const th of [thA, thB]) {
          const ux = Math.cos(th);
          const uy = Math.sin(th);
          const den = ux * dy - uy * dx;
          if (Math.abs(den) > 1e-12) consider((uy * Cx - ux * Cy) / den); // radial edges
        }
        return Number.isFinite(best) ? best : r2 - rBC;
      };
      // The 4 wedge corners (r1/r2 × thA/thB) with their bolt-local direction.
      // A fan step that straddles a corner chords across it → a corner-fill tri
      // closes the notch so it tiles seamlessly with the background quads.
      const corners = [
        [r1, thA],
        [r1, thB],
        [r2, thA],
        [r2, thB],
      ].map(([rr, th]) => {
        const kx = rr * Math.cos(th);
        const ky = rr * Math.sin(th);
        let psi = Math.atan2(ky - Cy, kx - Cx);
        if (psi < 0) psi += 2 * Math.PI;
        return { pt: { pos: [kx, ky, zHub], uv: planarUV(kx, ky), normal: NZ }, psi };
      });
      let Hp = null;
      let Fp = null;
      for (let j = 0; j <= bSeg; j++) {
        const phi = (j / bSeg) * 2 * Math.PI;
        const dx = Math.cos(phi);
        const dy = Math.sin(phi);
        const Hx = Cx + rBore * dx;
        const Hy = Cy + rBore * dy;
        const s = frameHit(dx, dy);
        const Fx = Cx + s * dx;
        const Fy = Cy + s * dy;
        const H = { pos: [Hx, Hy, zHub], uv: planarUV(Hx, Hy), normal: NZ };
        const F = { pos: [Fx, Fy, zHub], uv: planarUV(Fx, Fy), normal: NZ };
        if (Hp) {
          pushQuad('hub', [Fp, F, H, Hp]);
          const phiLo = ((j - 1) / bSeg) * 2 * Math.PI;
          for (const c of corners) {
            if (c.psi >= phiLo && c.psi < phi) pushTri('hub', [Fp, c.pt, F]);
          }
        }
        Hp = H;
        Fp = F;
      }
    }
  } else {
    emitBand('hub', { r: rBi, z: zHub, nr: 0, nz: 1 }, { r: rHub, z: zHub, nr: 0, nz: 1 });
  }

  // -- 6 counterbored socket-head bolts (front hub only) ---------------------
  function emitBolt(ang) {
    const bx = rBC * Math.cos(ang);
    const by = rBC * Math.sin(ang);
    for (let i = 0; i < bSeg; i++) {
      const a0 = (i / bSeg) * Math.PI * 2;
      const a1 = ((i + 1) / bSeg) * Math.PI * 2;
      const c0 = Math.cos(a0);
      const s0 = Math.sin(a0);
      const c1 = Math.cos(a1);
      const s1 = Math.sin(a1);
      const h0 = hexR(a0);
      const h1 = hexR(a1);
      // (1) counterbore outer wall (rBore): flush surface → gap floor, inward
      pushQuad(
        'hub',
        [
          { pos: [bx + rBore * c0, by + rBore * s0, zHub], uv: CUV, normal: [-c0, -s0, 0] },
          { pos: [bx + rBore * c0, by + rBore * s0, zBoreFloor], uv: CUV, normal: [-c0, -s0, 0] },
          { pos: [bx + rBore * c1, by + rBore * s1, zBoreFloor], uv: CUV, normal: [-c1, -s1, 0] },
          { pos: [bx + rBore * c1, by + rBore * s1, zHub], uv: CUV, normal: [-c1, -s1, 0] },
        ],
        AO_GAP,
      );
      // (2) clearance gap floor ring (rHead → rBore) at the pocket bottom
      pushQuad(
        'hub',
        [
          { pos: [bx + rHead * c0, by + rHead * s0, zBoreFloor], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + rBore * c0, by + rBore * s0, zBoreFloor], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + rBore * c1, by + rBore * s1, zBoreFloor], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + rHead * c1, by + rHead * s1, zBoreFloor], uv: CUV, normal: [0, 0, 1] },
        ],
        AO_GAP,
      );
      // (3) round head side wall (rHead): gap floor → up to the edge fillet start
      // (zWallTop). The head now stands PROUD, so the wall passes the hub plane.
      pushQuad(
        'hub',
        [
          { pos: [bx + rHead * c0, by + rHead * s0, zBoreFloor], uv: CUV, normal: [c0, s0, 0] },
          { pos: [bx + rHead * c0, by + rHead * s0, zWallTop], uv: CUV, normal: [c0, s0, 0] },
          { pos: [bx + rHead * c1, by + rHead * s1, zWallTop], uv: CUV, normal: [c1, s1, 0] },
          { pos: [bx + rHead * c1, by + rHead * s1, zBoreFloor], uv: CUV, normal: [c1, s1, 0] },
        ],
        AO_HEAD_WALL,
      );
      // (3b) head TOP OUTER-EDGE round-over: quarter arc rolling from the vertical
      // wall (rHead, zWallTop) inward+up to the top plane (rHead−headEdgeR, zHead).
      for (let k = 0; k < heSeg; k++) {
        const p0 = (k / heSeg) * (Math.PI / 2);
        const p1 = ((k + 1) / heSeg) * (Math.PI / 2);
        const rr0 = rHead - headEdgeR + headEdgeR * Math.cos(p0);
        const rr1 = rHead - headEdgeR + headEdgeR * Math.cos(p1);
        const zz0 = zHead - headEdgeR + headEdgeR * Math.sin(p0);
        const zz1 = zHead - headEdgeR + headEdgeR * Math.sin(p1);
        const nrad0 = Math.cos(p0);
        const nz0 = Math.sin(p0);
        const nrad1 = Math.cos(p1);
        const nz1 = Math.sin(p1);
        pushQuad(
          'hub',
          [
            { pos: [bx + rr0 * c0, by + rr0 * s0, zz0], uv: CUV, normal: [nrad0 * c0, nrad0 * s0, nz0] },
            { pos: [bx + rr1 * c0, by + rr1 * s0, zz1], uv: CUV, normal: [nrad1 * c0, nrad1 * s0, nz1] },
            { pos: [bx + rr1 * c1, by + rr1 * s1, zz1], uv: CUV, normal: [nrad1 * c1, nrad1 * s1, nz1] },
            { pos: [bx + rr0 * c1, by + rr0 * s1, zz0], uv: CUV, normal: [nrad0 * c1, nrad0 * s1, nz0] },
          ],
          AO_FLAT,
        );
      }
      // (4) proud head top ring (hex opening → head edge fillet), +Z, bright
      const rHeadTop = rHead - headEdgeR;
      pushQuad(
        'hub',
        [
          { pos: [bx + h0 * c0, by + h0 * s0, zHead], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + rHeadTop * c0, by + rHeadTop * s0, zHead], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + rHeadTop * c1, by + rHeadTop * s1, zHead], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + h1 * c1, by + h1 * s1, zHead], uv: CUV, normal: [0, 0, 1] },
        ],
        AO_FLAT,
      );
      // (5) hex (allen) socket wall: head top → socket floor, inward, dark
      pushQuad(
        'hub',
        [
          { pos: [bx + h0 * c0, by + h0 * s0, zHead], uv: CUV, normal: [-c0, -s0, 0] },
          { pos: [bx + h0 * c0, by + h0 * s0, zHexFloor], uv: CUV, normal: [-c0, -s0, 0] },
          { pos: [bx + h1 * c1, by + h1 * s1, zHexFloor], uv: CUV, normal: [-c1, -s1, 0] },
          { pos: [bx + h1 * c1, by + h1 * s1, zHead], uv: CUV, normal: [-c1, -s1, 0] },
        ],
        AO_HEX,
      );
      // (6) hex socket floor fan, +Z, dark
      pushTri(
        'hub',
        [
          { pos: [bx, by, zHexFloor], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + h0 * c0, by + h0 * s0, zHexFloor], uv: CUV, normal: [0, 0, 1] },
          { pos: [bx + h1 * c1, by + h1 * s1, zHexFloor], uv: CUV, normal: [0, 0, 1] },
        ],
        AO_HEX,
      );
    }
  }
  for (let b = 0; b < boltCount; b++) {
    emitBolt((b / boltCount) * Math.PI * 2); // phase 0 → two bolts on the horizontal axis
  }
  } // end competition hub/bolts (economy uses the flange above)

  // --- assemble: brand → body → hub (contiguous, non-overlapping groups) ---
  const indices = idxBrand.concat(idxBody, idxHub);
  const groups = [
    { start: 0, count: idxBrand.length, materialIndex: MATERIAL_BRAND_FACE, name: 'BrandFace' },
    { start: idxBrand.length, count: idxBody.length, materialIndex: MATERIAL_BODY, name: 'body' },
    { start: idxBrand.length + idxBody.length, count: idxHub.length, materialIndex: MATERIAL_HUB, name: 'hub' },
  ];
  return {
    positions,
    normals,
    uvs,
    colors,
    tangents,
    indices,
    groups,
    triangleCount: indices.length / 3,
    vertexCount: state.vertCount,
    brandVertexRange: { start: 0, count: brandVertexCount },
    params,
  };
}

/**
 * Assembles a THREE.BufferGeometry from the pure mesh data. THREE is passed in
 * (not imported) so this module never pulls in the WebGL layer for node tests.
 *
 * @param {typeof import('three')} THREE
 * @param {Partial<typeof DEFAULT_BUMPER_OPTS>} [opts]
 * @returns {import('three').BufferGeometry}
 */
export function createBumperPlateGeometry(THREE, opts = {}) {
  const mesh = buildBumperPlateMesh(opts);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(mesh.positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(mesh.normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(mesh.uvs, 2));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(mesh.colors, 3));
  geometry.setAttribute('tangent', new THREE.Float32BufferAttribute(mesh.tangents, 4));
  geometry.setIndex(mesh.indices);
  for (const g of mesh.groups) {
    geometry.addGroup(g.start, g.count, g.materialIndex);
  }
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();
  geometry.userData.bumperPlate = { params: mesh.params, triangleCount: mesh.triangleCount };
  return geometry;
}
